import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Check, Clock3, LogOut, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CurrentUser } from "../lib/auth";
import { api } from "../lib/api";
import { ShiftAcceptModal } from "./ShiftAcceptModal";

export function OperationalShiftControl({ user }: { user: CurrentUser }) {
  const [open, setOpen] = useState(false);
  const [acceptTemplateId, setAcceptTemplateId] = useState<string | null>(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  const shift = useQuery({
    queryKey: ["operational-shift"],
    queryFn: api.currentOperationalShift,
    refetchInterval: 30_000,
  });
  const options = useQuery({
    queryKey: ["operational-shift-options"],
    queryFn: api.operationalShiftOptions,
    enabled: open,
  });
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["operational-shift"] }),
      client.invalidateQueries({ queryKey: ["dashboard"] }),
      client.invalidateQueries({ queryKey: ["settings"] }),
    ]);
  };
  const closeExpired = useMutation({
    mutationFn: () => api.closeOperationalShiftWithReport({
      acknowledgePending: true,
      notes: `Просроченная смена закрыта директором ${user.name} перед передачей ресепшн`,
    }),
    onSuccess: refresh,
  });
  const active = shift.data;
  const owned = active?.acceptedById === user.id;
  const expired = Boolean(active && new Date(active.scheduledEndAt).getTime() < Date.now());
  const canForceClose = Boolean(active && !owned && expired && user.role === "owner");
  const busy = closeExpired.isPending;

  return <div className="shift-control">
    <button className={`shift-control__trigger ${active ? "is-active" : ""}`} onClick={() => setOpen((value) => !value)} aria-expanded={open}>
      <Clock3 size={15} />
      <span>{active ? owned ? "Моя смена" : active.acceptedBy : "Принять смену"}</span>
      {active && <i />}
    </button>
    {open && <div className="shift-popover">
      <header><div><strong>Операционная смена</strong><span>Очередь задач ресепшн</span></div><button onClick={() => setOpen(false)} aria-label="Закрыть"><X size={15}/></button></header>
      {active ? <div className="shift-popover__active">
        <div className="shift-popover__status"><Check size={16}/><div><strong>{active.templateName}</strong><span>Принял: {active.acceptedBy}</span></div></div>
        <dl>
          <div><dt>До</dt><dd>{new Date(active.scheduledEndAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}</dd></div>
          <div><dt>В очереди</dt><dd>{active.pendingTasks}</dd></div>
        </dl>
        {active.handedOverFrom && <p><ArrowRightLeft size={13}/> Получена от: {active.handedOverFrom}</p>}
        {owned
          ? <button className="secondary-button shift-popover__action" disabled={busy} onClick={() => { setOpen(false); navigate("/shift"); }}><LogOut size={14}/> Сдать смену</button>
          : canForceClose
            ? <button className="secondary-button shift-popover__action" disabled={busy} onClick={() => {
              if (window.confirm(`Смена ${active.acceptedBy} просрочена. Закрыть её с аудитом и передать незавершённые задачи?`)) closeExpired.mutate();
            }}><LogOut size={14}/> Закрыть просроченную смену</button>
            : <p className="shift-popover__hint">{expired ? `Смена просрочена. Её сдаёт ${active.acceptedBy} или закрывает директор.` : `Сдать эту смену может только ${active.acceptedBy} или директор.`}</p>}
      </div> : <div className="shift-popover__empty"><p>Смена ещё не принята. Выберите рабочий интервал:</p></div>}
      {!active && <div className="shift-option-list">
        {options.data?.map((option) => <button key={option.id} disabled={busy} onClick={() => { setOpen(false); setAcceptTemplateId(option.id); }}>
          <span><strong>{option.name}</strong><small>{option.start} — {option.end}</small></span>
          <ArrowRightLeft size={15}/>
        </button>)}
        {options.isPending && <div className="skeleton shift-option-skeleton" />}
      </div>}
      {closeExpired.error && <div className="form-error" role="alert">{closeExpired.error.message}</div>}
    </div>}
    {acceptTemplateId && options.data && <ShiftAcceptModal options={options.data} initialTemplateId={acceptTemplateId} onClose={() => setAcceptTemplateId(null)}/>}
  </div>;
}
